import { Link } from 'react-router-dom';
import {
  ShieldCheck, Bell, Search, FileCheck, Clock, Building2,
  MessageSquare, ClipboardList, Star, ArrowRight, Users, LineChart,
} from 'lucide-react';

const CONTAINER = 'max-w-6xl mx-auto px-6 lg:px-8';
const H1 = 'text-4xl sm:text-5xl md:text-6xl font-bold tracking-tight text-[#0B132B]';
const H2 = 'text-3xl sm:text-4xl md:text-5xl font-semibold tracking-tight text-[#0B132B]';
const EYEBROW = 'text-[11px] font-bold uppercase tracking-[0.22em] text-[#DA291C]';
const BODY = 'text-base sm:text-lg text-slate-500 leading-relaxed';

const FEATURES = [
  { icon: ShieldCheck, title: 'Verified worker profiles', body: 'Every worker is checked before they join. Managers see identity verification, Smart ID status and a live reliability score.' },
  { icon: LineChart, title: 'Reliability history', body: 'Transparent scores updated after every shift so you always know who you can rely on when it matters.' },
  { icon: Bell, title: 'Real-time alerts', body: 'Get notified the moment someone applies, cancels or sends a message — mobile-first, never intrusive.' },
  { icon: Search, title: 'Fast matching', body: 'Filter by location, availability, experience, reliability and rate — find the right person in seconds.' },
  { icon: FileCheck, title: 'Till & shift audit trail', body: 'Timestamped till checks every three hours plus a full shift record ready for HR or payroll.' },
  { icon: Clock, title: 'Same-day cover', body: 'Post a shift starting in hours, not days. Verified workers respond in real time.' },
  { icon: Building2, title: 'Multi-site support', body: 'Manage many branches under one account with a shared worker pool and consolidated billing.' },
  { icon: MessageSquare, title: 'In-app messaging', body: 'Talk to applicants and confirmed workers directly — no swapping numbers, no missed messages.' },
  { icon: ClipboardList, title: 'Shift records', body: 'Every shift is captured start-to-finish with timestamped events — great for compliance and audits.' },
  { icon: Star, title: 'Two-way ratings', body: 'Both sides rate each shift so trust compounds over time and everyone knows what to expect.' },
  { icon: Users, title: 'Worker pool', body: 'Save workers you love and invite them back for future shifts with one tap.' },
  { icon: ShieldCheck, title: 'GDPR & security', body: 'UK-based data handling with consent audit trails, right-to-erasure and role-based access controls.' },
];

export default function FeaturesPage() {
  return (
    <div data-testid="features-page">
      <section className={`${CONTAINER} pt-32 pb-16 lg:pt-40 lg:pb-20`}>
        <div className="max-w-3xl">
          <p className={EYEBROW}>Features</p>
          <h1 className={`${H1} mt-5`}>Everything you need for trusted cover.</h1>
          <p className={`${BODY} mt-6 max-w-xl`}>
            The features managers actually reach for every day — no bloat, no analytics theatre.
          </p>
        </div>
      </section>

      <section className={`${CONTAINER} pb-24 lg:pb-32`}>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {FEATURES.map((f) => (
            <div key={f.title} className="rounded-3xl bg-white border border-slate-200/70 p-8 hover:-translate-y-0.5 hover:shadow-[0_16px_40px_rgba(11,19,43,0.06)] transition-[transform,box-shadow] duration-300" data-testid={`feature-detail-${f.title.replace(/\s+/g, '-').toLowerCase()}`}>
              <div className="h-11 w-11 rounded-2xl bg-[#0B132B] flex items-center justify-center">
                <f.icon className="h-5 w-5 text-white" strokeWidth={1.8} />
              </div>
              <h2 className="mt-6 text-xl font-semibold text-[#0B132B] tracking-tight">{f.title}</h2>
              <p className="mt-3 text-[14.5px] text-slate-500 leading-relaxed">{f.body}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-slate-50/70 py-20 lg:py-24">
        <div className={`${CONTAINER} text-center max-w-2xl`}>
          <h2 className={`${H2}`}>See it in action.</h2>
          <p className={`${BODY} mt-4`}>Start your 14-day free trial today. No card required.</p>
          <Link to="/register?role=manager" className="mt-8 inline-flex items-center gap-2 h-14 px-8 rounded-full bg-[#DA291C] text-white font-semibold hover:bg-[#b91f13] transition-colors" data-testid="features-cta">
            Start Free Trial <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}
